import { useEffect, useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Filter, Pencil, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CategoryFilter({ active, onChange, autoPromptFirstLogin = false }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const { data } = useQuery({
    queryKey: ["categories"],
    queryFn: () => axios.get(`${API}/api/auth/categories`, { withCredentials: true }).then((r) => r.data),
  });
  const saved = data?.categories || [];
  const available = data?.available || [];

  useEffect(() => {
    if (data) onChange(saved.filter((c) => !(active?.off || []).includes(c)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (autoPromptFirstLogin && data && saved.length === 0) setEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const save = useMutation({
    mutationFn: (cats) =>
      axios.put(`${API}/api/auth/categories`, { categories: cats }, { withCredentials: true }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success("Interest categories saved");
      qc.invalidateQueries({ queryKey: ["categories"] });
      onChange(d.categories);
      setEditing(false);
    },
  });

  const [off, setOff] = useState([]); // per-query toggles, defaults untouched
  const toggle = (c) => {
    const next = off.includes(c) ? off.filter((x) => x !== c) : [...off, c];
    setOff(next);
    onChange(saved.filter((x) => !next.includes(x)));
  };

  return (
    <div data-testid="category-filter" className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-500">
        <Filter size={10} /> LENS
      </span>
      {saved.length === 0 && (
        <span className="text-[10px] font-mono text-zinc-600">no categories set</span>
      )}
      {saved.map((c) => {
        const on = !off.includes(c);
        return (
          <button key={c} data-testid={`category-tag-${c.toLowerCase()}`} onClick={() => toggle(c)}
                  title="Toggle for this query only"
                  className={"px-2 h-6 text-[10px] font-mono border " +
                    (on ? "border-emerald-400/60 bg-emerald-500/10 text-emerald-200"
                        : "border-[#27272A] text-zinc-600 line-through")}>
            {c}
          </button>
        );
      })}
      <button data-testid="category-edit-button" onClick={() => { setDraft(saved); setEditing(true); }}
              className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono text-zinc-400 hover:text-white border border-[#27272A]">
        <Pencil size={10} /> EDIT
      </button>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="category-dialog">
          <div className="w-full max-w-md border border-[#27272A] bg-[#0A0A0A] p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400">
              PERSONALIZED_CATEGORIES
            </div>
            <h3 className="mt-1 text-white font-mono text-lg">Select your areas of interest</h3>
            <p className="mt-1 text-[12px] text-zinc-400">
              The synthesis engine prioritises cross-domain connections relevant
              to these fields. Edit anytime.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {available.map((c) => {
                const sel = draft.includes(c);
                return (
                  <button key={c} data-testid={`category-option-${c.toLowerCase()}`}
                          onClick={() => setDraft(sel ? draft.filter((x) => x !== c) : [...draft, c])}
                          className={"h-9 text-[11px] font-mono border flex items-center justify-center gap-1 " +
                            (sel ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-[#27272A] text-zinc-400 hover:text-white")}>
                    {sel && <Check size={11} />} {c}
                  </button>
                );
              })}
            </div>
            <div className="mt-5 flex gap-2">
              <button data-testid="category-save-button" onClick={() => save.mutate(draft)} disabled={save.isPending}
                      className="flex-1 h-10 bg-amber-400 text-black font-mono uppercase text-[11px] tracking-wider hover:bg-amber-300">
                SAVE_CATEGORIES
              </button>
              <button data-testid="category-cancel-button" onClick={() => setEditing(false)}
                      className="px-4 h-10 border border-[#27272A] text-zinc-400 font-mono uppercase text-[11px] hover:text-white">
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
